import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { Cacheable, CacheBuster } from 'ts-cacheable';

import { url } from '../helpers/url';

import { SharedSettings } from '../models/shared-settings';
import { Carrier } from '../models/carrier';

@Injectable({
    providedIn: 'root'
})
export class CarrierService {
    private static readonly cacheObservable = new Subject<void>();

    constructor(private http: HttpClient, private settings: SharedSettings) { }

    @Cacheable({
        cacheBusterObserver: CarrierService.cacheObservable,
        maxAge: 5 * 60 * 1000
    })
    getCarriers(): Observable<Carrier[]> {
        return this.http.get<Carrier[]>(this.settings.apiBaseUrl + 'carriers')
            .pipe(map(carriers => carriers.map(this.calcDisplayName)));
    }

    getCarrier(carrierCode: string): Observable<Carrier> {
        return this.http.get<Carrier>(this.settings.apiBaseUrl + url`carriers/${carrierCode}`)
            .pipe(map(carrier => this.calcDisplayName(carrier)));
    }

    @CacheBuster({
        cacheBusterNotifier: CarrierService.cacheObservable
    })
    createCarrier(carrier: Carrier): Observable<Carrier> {
        return this.http.post<Carrier>(this.settings.apiBaseUrl + 'carriers', carrier)
            .pipe(map(c => this.calcDisplayName(c)));
    }

    @CacheBuster({
        cacheBusterNotifier: CarrierService.cacheObservable
    })
    updateCarrier(carrier: Carrier): Observable<Carrier> {
        return this.http.put<Carrier>(this.settings.apiBaseUrl + url`carriers`, carrier)
            .pipe(map(c => this.calcDisplayName(c)));
    }

    @CacheBuster({
        cacheBusterNotifier: CarrierService.cacheObservable
    })
    replaceCarrier(carrier: Carrier, replacement: Carrier): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl
            + url`carriers/${carrier.carrierCode}?replaceWith=${replacement.carrierCode}`);
    }

    @CacheBuster({
        cacheBusterNotifier: CarrierService.cacheObservable
    })
    deleteCarrier(carrier: Carrier): Observable<void> {
        return this.http.delete<void>(this.settings.apiBaseUrl + url`carriers/${carrier.carrierCode}`);
    }

    private calcDisplayName(carrier: Carrier): Carrier {
        if (carrier)
            carrier.carrierCodeName = carrier.carrierCode ? carrier.carrierCode + ' - ' + carrier.carrierName : carrier.carrierName;

        return carrier;
    }
}
